import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useStoreRequests } from '@/core/composables/useStoreRequests'
import { useStoreEditingDialogs } from '@/core/composables/useStoreEditingDialogs'
import { DriverGradesRepository } from '../repositories/DriverGrades.repository'
import { TDriverGrade, TDriverGradeDto, TGradeHistoryEntry } from '../models/DriverGrade.model'
import { Message, MessageBox } from 'element-ui'

export const useDriverGradesStore = defineStore('driverGrades', () => {
  const { isLoading, createRequest } = useStoreRequests()
  const repository = new DriverGradesRepository()

  const grades = ref<TDriverGrade[]>([])
  const currentGrade = ref<TDriverGrade>()
  const dialogs = useStoreEditingDialogs(currentGrade)

  // Override state
  const overrideDriverId = ref('')
  const overrideDriverName = ref('')
  const overrideDriverCurrentGradeId = ref('')
  const overrideScheduleId = ref('')
  const overrideScheduleDate = ref('')
  const isOverrideDialogVisible = ref(false)
  const isGradeHistoryVisible = ref(false)
  const gradeHistory = ref<TGradeHistoryEntry[]>([])
  const isHistoryLoading = ref(false)

  const gradesMap = computed(() => {
    const map: Record<string, TDriverGrade> = {}
    for (const grade of grades.value) {
      map[grade.id] = grade
    }
    return map
  })

  const fetchGrades = createRequest(async () => {
    grades.value = await repository.fetchGrades()
  })

  const createGrade = createRequest(async (dto: TDriverGradeDto) => {
    await repository.createGrade(dto)
    await fetchGrades()

    Message({ message: 'Грейд успешно создан', type: 'success', duration: 3000 })
  })

  const updateGrade = createRequest(async (dto: TDriverGradeDto) => {
    if (!currentGrade.value) return

    await repository.updateGrade(currentGrade.value.id, dto)
    await fetchGrades()

    Message({ message: 'Грейд успешно обновлён', type: 'success', duration: 3000 })
  })

  const deleteGrade = async (id: string) => {
    try {
      await MessageBox.confirm('Вы уверены, что хотите удалить грейд?', 'Внимание', {
        confirmButtonText: 'Да',
        cancelButtonText: 'Отмена',
        type: 'warning',
      })

      await repository.deleteGrade(id)
      await fetchGrades()

      Message({ message: 'Грейд успешно удалён', type: 'success', duration: 3000 })
    } catch {
      // cancel
    }
  }

  // Override
  const openOverrideDialog = (
    driverId: string,
    driverName: string,
    currentGradeId: string,
    scheduleId?: string,
    scheduleDate?: string,
  ) => {
    overrideDriverId.value = driverId
    overrideDriverName.value = driverName
    overrideDriverCurrentGradeId.value = currentGradeId
    overrideScheduleId.value = scheduleId ?? ''
    overrideScheduleDate.value = scheduleDate ?? ''
    isOverrideDialogVisible.value = true
  }

  const closeOverrideDialog = () => {
    isOverrideDialogVisible.value = false
    overrideDriverId.value = ''
    overrideDriverName.value = ''
    overrideDriverCurrentGradeId.value = ''
    overrideScheduleId.value = ''
    overrideScheduleDate.value = ''
  }

  const openGradeHistory = async () => {
    isGradeHistoryVisible.value = true
    if (!overrideDriverId.value) return

    isHistoryLoading.value = true
    try {
      gradeHistory.value = await repository.fetchGradeHistory(overrideDriverId.value)
    } catch {
      gradeHistory.value = []
    } finally {
      isHistoryLoading.value = false
    }
  }

  const closeGradeHistory = () => {
    isGradeHistoryVisible.value = false
    gradeHistory.value = []
  }

  const overrideGrade = createRequest(async (driverId: string, gradeId: string) => {
    await repository.overrideGrade(driverId, gradeId)

    if (overrideScheduleId.value && overrideScheduleDate.value) {
      await repository.updateScheduleTask(overrideScheduleId.value, overrideScheduleDate.value)
    }

    Message({
      message: gradeId ? 'Грейд успешно изменён' : 'Грейд сброшен',
      type: 'success',
      duration: 3000,
    })
  })

  return {
    isLoading,
    grades,
    currentGrade,
    gradesMap,
    dialogs,
    overrideDriverId,
    overrideDriverName,
    overrideDriverCurrentGradeId,
    overrideScheduleId,
    overrideScheduleDate,
    isOverrideDialogVisible,
    isGradeHistoryVisible,
    gradeHistory,
    isHistoryLoading,
    fetchGrades,
    createGrade,
    updateGrade,
    deleteGrade,
    openOverrideDialog,
    closeOverrideDialog,
    openGradeHistory,
    closeGradeHistory,
    overrideGrade,
  }
})
